import { useCallback, useEffect, useRef, useState } from "react";
import {
  FiChevronDown,
  FiUser,
  FiSettings,
  FiLock,
  FiLogOut,
  FiSearch,
  FiBell,
  FiMenu,
} from "react-icons/fi";
import ChangePasswordModal from "../common/ChangePasswordModal";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";

const quickLinks = [
  ["Dashboard", "/admindashboard", "dashboard home overview"],
  ["Employees", "/admindashboard/employees", "employees hrms staff", "employees.view"],
  ["Attendance overview", "/admindashboard/attendance", "attendance shifts corrections", "attendance.view_team"],
  ["Products", "/admindashboard/masters/products", "catalogue products variants barcodes", "products.view"],
  ["Barcode labels", "/admindashboard/inventory/barcode-labels", "barcode labels print reprint product sku", "barcode_labels.view"],
  ["Inventory", "/admindashboard/inventory", "inventory stock lots", "inventory.view"],
  ["Purchase orders", "/admindashboard/purchases/orders", "purchase orders po", "purchases.view"],
  ["Goods receipts", "/admindashboard/purchases/grn", "grn goods receipt purchases", "purchases.receive"],
  ["Supplier returns", "/admindashboard/purchases/returns", "supplier return debit note", "purchases.view"],
  ["POS bills", "/admindashboard/sales/pos-bills", "pos cashier bills receipts", "pos.view_reports"],
  ["Sales orders", "/admindashboard/sales/orders/list", "sales orders customers", "sales_orders.view"],
  ["Business reports", "/admindashboard/reports/sales-detailed", "reports sales gst stock", "reports.sales_detailed.view"],
  ["GST & HSN", "/admindashboard/masters/tax-catalog", "gst hsn tax", "tax_catalog.view"],
  ["Import history", "/admindashboard/tools/importhistory", "imports excel history", "imports.view"],
  ["Audit logs", "/admindashboard/tools/auditlogs", "audit logs activity", "audit_logs.view"],
  ["Company settings", "/admindashboard/settings", "company settings prefixes", "settings.view"],
];

const notificationIcons = {
  LEAVE: "📝",
  ATTENDANCE: "⏰",
  PURCHASE: "📦",
  RETURN: "↩️",
  PAYROLL: "💰",
  AUDIT: "📋",
};

function relativeTime(value) {
  if (!value) return "Pending action";
  const minutes = Math.max(
    0,
    Math.round((Date.now() - new Date(value).getTime()) / 60_000),
  );
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function Navbar({ sidebarCollapsed = false, onToggleSidebar }) {
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const notificationRef = useRef(null);
  const profileRef = useRef(null);
  const searchRef = useRef(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const { user, logout, accessToken } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationTotal, setNotificationTotal] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  const displayName =
    user?.employee?.fullName || user?.email?.split("@")[0] || "Administrator";

  const roleName =
    user?.roles?.[0]?.role?.name || user?.roles?.[0]?.name || "Administrator";

  const initials = displayName
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const handleLogout = async () => {
    setIsLoggingOut(true);

    try {
      await logout();
    } catch {
      // Local auth state is cleared even if the network request fails.
    } finally {
      navigate("/admin/login", { replace: true });
      setIsLoggingOut(false);
    }
  };

  const loadNotifications = useCallback(async () => {
    if (!accessToken) return;

    try {
      setNotificationsLoading(true);
      const response = await apiRequest("/notifications", { accessToken });
      setNotifications(response?.notifications || []);
      setNotificationTotal(Number(response?.total || 0));
    } catch {
      setNotifications([]);
      setNotificationTotal(0);
    } finally {
      setNotificationsLoading(false);
    }
  }, [accessToken]);

  const getGreeting = () => {
    const hour = new Date().getHours();

    if (hour >= 5 && hour < 12) {
      return "Good Morning 👋";
    }

    if (hour >= 12 && hour < 17) {
      return "Good Afternoon ☀️";
    }

    return "Good Evening 🌙";
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(e.target)
      ) {
        setShowNotifications(false);
      }

      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfile(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearch(false);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    loadNotifications();
    const intervalId = window.setInterval(loadNotifications, 60_000);
    return () => window.clearInterval(intervalId);
  }, [loadNotifications]);

  const searchResults = quickLinks.filter(([title, , keywords, permission]) => {
    if (permission && !hasPermission(user, permission)) return false;
    const query = searchQuery.trim().toLowerCase();
    return query && `${title} ${keywords}`.toLowerCase().includes(query);
  }).slice(0, 7);

  const openSearchResult = (path) => {
    navigate(path);
    setSearchQuery("");
    setShowSearch(false);
  };

  return (
    <header
      className="
    fixed
    top-0
    right-0
    h-20
    bg-white/95
    backdrop-blur-md
    border-b
    border-gray-100
    flex
    items-center
    justify-between
    px-8
    z-40
    shadow-sm
    transition-[left]
    duration-200
  "
      style={{ left: sidebarCollapsed ? "5rem" : "18rem" }}
    >
      <div className="flex items-center gap-4 lg:gap-8">
        <button
          type="button"
          onClick={onToggleSidebar}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 transition hover:border-primary-200 hover:bg-primary-50 hover:text-primary-800"
          title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <FiMenu size={20} />
        </button>
        <div className="hidden xl:block">
          <h2 className="text-lg font-semibold text-gray-900">
            {getGreeting()}
          </h2>

          <p className="text-sm text-gray-500">
            Here's what's happening today.
          </p>
        </div>
        <div className="relative w-[420px] max-w-full" ref={searchRef}>
          <FiSearch
            size={18}
            className="
    absolute
    left-4
    top-1/2
    -translate-y-1/2
    text-gray-400
  "
          />

          <input
            type="text"
            value={searchQuery}
            onFocus={() => setShowSearch(true)}
            onChange={(event) => { setSearchQuery(event.target.value); setShowSearch(true); }}
            onKeyDown={(event) => {
              if (event.key === "Enter" && searchResults[0]) openSearchResult(searchResults[0][1]);
              if (event.key === "Escape") setShowSearch(false);
            }}
            placeholder="Search pages, actions and tools..."
            className="
    w-full
    pl-12
    pr-4
    py-3
    bg-gray-50
    border
    border-gray-100
    rounded-2xl
    text-sm
    placeholder:text-gray-400
    transition-all
    duration-200
    focus:outline-none
    focus:border-primary-300
    focus:ring-4
    focus:ring-primary-50
    focus:bg-white
  "
          />
          {showSearch && searchQuery.trim() ? (
            <div className="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-50 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.16)]">
              {searchResults.length ? searchResults.map(([title, path, , permission]) => (
                <button key={path} type="button" onClick={() => openSearchResult(path)} className="flex w-full items-center justify-between gap-4 border-b border-gray-50 px-4 py-3 text-left last:border-0 hover:bg-primary-50">
                  <span><span className="block text-sm font-semibold text-gray-900">{title}</span><span className="mt-0.5 block text-xs text-gray-500">{path.replace('/admindashboard', '') || '/admindashboard'}</span></span>
                  {permission ? <span className="rounded-full bg-gray-100 px-2 py-1 text-[10px] font-medium text-gray-500">Access allowed</span> : null}
                </button>
              )) : <p className="px-4 py-5 text-sm text-gray-500">No accessible page matches “{searchQuery}”.</p>}
            </div>
          ) : null}
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-6">
        <div className="relative" ref={notificationRef}>
          <button
            onClick={() => {
              setShowNotifications((prev) => !prev);
              setShowProfile(false);
              if (!showNotifications) loadNotifications();
            }}
            className="
      relative
      h-11
      w-11
      rounded-2xl
      bg-gray-50
      flex
      items-center
      justify-center
      transition-all
      hover:bg-primary-50
      hover:text-primary-700
    "
          >
            <FiBell
              size={20}
              className={`
        transition-colors
        ${showNotifications ? "text-primary-700" : "text-gray-600"}
      `}
            />

            {notificationTotal > 0 ? <span
              className="
        absolute
        top-2
        right-2
        h-2.5
        w-2.5
        rounded-full
        bg-primary-600
        ring-2
        ring-white
      "
            /> : null}
          </button>

          {showNotifications && (
            <div
              className="
        absolute
        right-0
        top-14
        w-80
        bg-white
        rounded-3xl
        border
        border-gray-100
        shadow-[0_20px_50px_rgba(0,0,0,0.08)]
        overflow-hidden
        z-50
        animate-in
        fade-in
        slide-in-from-top-2
        duration-200
      "
            >
              <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
                <h3 className="font-semibold text-gray-900">Notifications</h3>

                <span className="text-xs px-2 py-1 rounded-full bg-primary-50 text-primary-700">
                  {notificationTotal}
                </span>
              </div>

              {notificationsLoading && !notifications.length ? (
                <div className="px-5 py-8 text-center text-sm text-gray-500">
                  Loading notifications…
                </div>
              ) : null}

              {!notificationsLoading && !notifications.length ? (
                <div className="px-5 py-8 text-center text-sm text-gray-500">
                  No notifications for your current permissions.
                </div>
              ) : null}

              {notifications.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    setShowNotifications(false);
                    navigate(item.href);
                  }}
                  className="
          w-full
          px-5
          py-4
          flex
          gap-4
          hover:bg-gray-50
          transition-all
        "
                >
                  <div className="text-xl">
                    {notificationIcons[item.kind] || "🔔"}
                  </div>

                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-800">
                      {item.count} {item.title.toLowerCase()}
                    </p>

                    <p className="mt-1 text-xs text-gray-500">
                      {item.description}
                    </p>
                    <p className="mt-1 text-xs text-gray-400">
                      {relativeTime(item.createdAt)}
                    </p>
                  </div>
                </button>
              ))}

              <button
                type="button"
                onClick={loadNotifications}
                disabled={notificationsLoading}
                className="
        w-full
        py-4
        text-sm
        font-medium
        text-primary-700
        hover:bg-primary-50
      "
              >
                {notificationsLoading ? "Refreshing…" : "Refresh notifications"}
              </button>
            </div>
          )}
        </div>

        <div className="relative" ref={profileRef}>
          <button
            onClick={() => {
              setShowProfile((prev) => !prev);
              setShowNotifications(false);
            }}
            className="
      flex
      items-center
      gap-3
      px-3
      py-2
      rounded-2xl
      transition-all
      hover:bg-gray-50
    "
          >
            <div
              className="
        h-12
        w-12
        rounded-2xl
        bg-gradient-to-br
        from-primary-700
        to-primary-800
        text-white
        flex
        items-center
        justify-center
        font-semibold
        shadow-lg
        shadow-primary-100
      "
            >
              {initials}
            </div>

            <div className="text-left">
              <p className="max-w-32 truncate font-semibold text-gray-900">
                {displayName}
              </p>

              <p className="max-w-32 truncate text-xs text-gray-400">
                {roleName}
              </p>
            </div>

            <FiChevronDown
              className={`
        text-gray-400
        transition-transform
        duration-300
        ${showProfile ? "rotate-180 text-primary-700" : ""}
      `}
            />
          </button>

          {showProfile && (
            <div
              className="
        absolute
        right-0
        top-16
        w-64
        bg-white
        rounded-3xl
        border
        border-gray-100
        shadow-[0_20px_50px_rgba(0,0,0,0.08)]
        overflow-hidden
        z-50
        animate-in
        fade-in
        slide-in-from-top-2
        duration-200
      "
            >
              <div className="px-5 py-4 border-b border-gray-100">
                <p className="max-w-32 truncate font-semibold text-gray-900">
                  {displayName}
                </p>

                <p className="max-w-32 truncate text-xs text-gray-400">
                  {roleName}
                </p>
              </div>

              <button
                className="w-full px-5 py-3 flex items-center gap-3 hover:bg-gray-50"
                onClick={() => navigate("/admindashboard/settings")}
              >
                <FiSettings />
                Account Settings
              </button>

              <button
                className="w-full px-5 py-3 flex items-center gap-3 hover:bg-gray-50"
                onClick={() => setShowPasswordModal(true)}
              >
                <FiLock />
                Change Password
              </button>

              <div className="border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="w-full px-5 py-3 flex items-center gap-3 text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FiLogOut />
                  {isLoggingOut ? "Logging out..." : "Logout"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <ChangePasswordModal
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
    </header>
  );
}
